import { logDebug, logError, logWarn } from '@ptek/client-logger';
import {
  useWidgetContext,
  useWidgetEvents,
  useWidgetExpandCollapse,
} from '@ptek/widget-core';
import { ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import * as audioRecorder from '../api-clients/audioRecorder';
import { documentTemplatesClient } from '../api-clients/documentTemplates';
import { sessionsClient, SessionWithDocuments } from '../api-clients/sessions';
import { readOrCreatePersistedRecordingDeviceId } from '../api-clients/recordingLeaseStorage';
import { WhisperClient } from '../api-clients/whisperClient';
import {
  AppContext,
  AppSharedContext,
  INITIAL_SESSION_MODEL,
  SessionModel,
} from '../context/AppContext';
import { WidgetAuthContextType } from '../context/AuthContext';
import { WIDGET_EVENTS } from '../constants/events';
import { TAB_IDS } from '../constants/app';
import { useUserpilotTracking } from '../hooks';
import { useDocumentGeneration } from '../hooks/useDocumentGeneration';
import { useAutoSelectDocumentTab } from '../hooks/useAutoSelectDocumentTab';
import { useAutoExportDocument } from '../features/Document/hooks/useAutoExportDocument';
import { createDocumentState } from '../features/Document/utils/documentContentUtils';
import { useRecordingStore } from '../stores';
import { AmbientScribeContext } from '../types';
import { FEATURE_FLAGS } from '../types/featureFlags';
import {
  featureFlagStates,
  getAutoExportConfigFromContext,
  isFeatureEnabled,
} from '../utils/feature-util';
import { ContestEntriesStatus } from '../api-clients/contestEntries';
import { ContestStatus } from '../types/contest';
import { HealthStatus } from '../types/healthStatus';
import { FormattedTemplate } from '../types/templates';
import { TRACK_EVENTS } from '../userpilot';
import { processDocumentData, processSessionData } from '../utils';
import { buildContextForPipeline } from '../utils/buildContextForPipeline';
import {
  buildContextItems,
  buildWidgetConfigFlags,
} from '../utils/buildStartSetup';
import { formatElapsedMs } from '../utils/duration';
import { getDefaultTemplateId } from '../utils/templateUtils';
import { startScribeJobPollFallback } from '../utils/scribeJobPollFallback';
import { TranscriptProcessor } from '../utils/transcriptUtils';
import {
  loadUserSettingsPreferences,
  saveUserSettingsPreferences,
} from '../utils/userPreferences';
import { AppLayout } from './AppLayout';
import { ConfettiCelebration } from './ConfettiCelebration';
import { EventHandler } from './EventHandler';

const DOCGEN_REQUEST_POLL_INITIAL_INTERVAL_MS = 1_000;
const DOCGEN_REQUEST_POLL_MAX_INTERVAL_MS = 10_000;
// This poll only guards the pre-job window: a healthy request reaches ENQUEUED
// in seconds, after which the job poll (its own 120s expiry in
// scribeJobPollFallback) takes over. Cap it at the same 120s so a request the
// server never finalizes (stale/superseded take, no FAILED written) resolves to
// the failure state instead of spinning indefinitely (NEB-100535).
const DOCGEN_REQUEST_POLL_MAX_MS = 2 * 60_000;

/**
 * Who is applying session documents. Only 'hydration' (reopening the current
 * encounter) may adopt the session into the recording store; a 'background'
 * completion (job poll, post-subscribe reconcile, WS push) never does.
 */
type SessionDocumentsOrigin = 'hydration' | 'background';

const buildTemplatePayload = (
  template: FormattedTemplate,
  templateId?: string
): Record<string, unknown> => {
  const rawTemplate = template.template as Record<string, unknown> | undefined;
  return {
    ...rawTemplate,
    templateId:
      templateId ??
      template.id ??
      (rawTemplate?.templateId as string | undefined) ??
      (rawTemplate?.id as string | undefined),
  };
};

interface AppControllerProps {
  auth: WidgetAuthContextType;
  templates?: FormattedTemplate[];
  healthStatus: HealthStatus | null;
  contestStatus: ContestStatus;
  contestEntries: ContestEntriesStatus;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  handleTabChange?: (tab: string) => void;
  userSwitchedTabRef?: { current: boolean };
}

export const AppController: ComponentType<AppControllerProps> = ({
  auth,
  templates,
  healthStatus,
  contestStatus,
  contestEntries,
  activeTab: propsActiveTab,
  setActiveTab: propsSetActiveTab,
  handleTabChange: propsHandleTabChange,
  userSwitchedTabRef: propsUserSwitchedTabRef,
}) => {
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const { expand } = useWidgetExpandCollapse();
  const { emitToHost } = useWidgetEvents();
  const [sessionModel, setSessionModel] = useState<SessionModel>(
    INITIAL_SESSION_MODEL
  );
  const [internalActiveTab, setInternalActiveTab] = useState<string>(
    TAB_IDS.transcript
  );
  const internalUserSwitchedTabRef = useRef(false);
  const activeTab = propsActiveTab ?? internalActiveTab;
  const setActiveTab = propsSetActiveTab ?? setInternalActiveTab;
  const userSwitchedTabRef =
    propsUserSwitchedTabRef ?? internalUserSwitchedTabRef;
  const internalHandleTabChange = useCallback(
    (newTabId: string) => {
      userSwitchedTabRef.current = true;
      expand();
      setActiveTab(newTabId);
    },
    [expand, setActiveTab, userSwitchedTabRef]
  );
  const tabChangeHandler = propsHandleTabChange ?? internalHandleTabChange;
  const hideSoapNotesTab =
    getAutoExportConfigFromContext(widgetContext.context)?.hideSoapNotesTab ===
    true;

  useAutoSelectDocumentTab({
    documentsLength: sessionModel.documents.length,
    hideSoapNotesTab,
    userSwitchedTabRef,
    setActiveTab,
  });
  const isCurrentlyRecording = useRecordingStore((state) => state.isRecording);
  const unifiedSessionIdEnabled = useRecordingStore(
    (state) => state.unifiedSessionIdEnabled
  );
  /** Unified finalize always enqueues server-side, so recovery must not depend on the legacy BG flag. */
  const backgroundRecoveryEnabled =
    isFeatureEnabled(
      widgetContext.context?.features,
      FEATURE_FLAGS.SCRIBE_BACKGROUND_GENERATION
    ) || unifiedSessionIdEnabled;
  const includeLastTakeInHydration = unifiedSessionIdEnabled;
  const snapshotVersionRef = useRef(0);
  const latestSelectedTemplateIdRef = useRef('');
  const latestTemplateSelectionRequestRef = useRef(0);
  const exactAwaitJobIdRef = useRef<string | null>(null);
  /**
   * Load-bearing (G2/G4): the arm's EXISTENCE suppresses auto-export on cold
   * reopen (only a stop/enqueue path sets it), and `exported` enforces one-shot
   * per session. performAutoExport has no internal idempotency — do not reduce
   * this to a document-id key.
   */
  const autoExportArmRef = useRef<{
    sessionId: string;
    appointmentId: string;
    exported: boolean;
  } | null>(null);
  const exactAwaitTakeGenerationRef = useRef<number | null>(null);
  const hydratedSessionRef = useRef<{
    appointmentId: string;
    sessionId: string;
  } | null>(null);
  const activeAppointmentIdRef = useRef('');
  activeAppointmentIdRef.current = widgetContext.context.appointment?.id || '';
  const disposedRef = useRef(false);
  const appointmentPollCancelsRef = useRef(new Set<() => void>());
  // Appointment only, never the host sessionId: the widget mints its own
  // sessions (legacy Record, unified "Discard & start new") and the host is not
  // required to echo them back.
  const isCurrentAppointment = useCallback((appointmentId: string) => {
    if (disposedRef.current) return false;
    // useEncounterHandler switches the store synchronously with updateContext;
    // the render-time ref only catches up on AppController's next render.
    const storeAppointmentId =
      useRecordingStore.getState().activeRecordingSession?.id;
    if (storeAppointmentId && storeAppointmentId !== appointmentId) {
      return false;
    }
    return activeAppointmentIdRef.current === appointmentId;
  }, []);
  const registerAppointmentPollCancel = useCallback((cancel: () => void) => {
    let registeredCancel = () => {};
    registeredCancel = () => {
      cancel();
      appointmentPollCancelsRef.current.delete(registeredCancel);
    };
    appointmentPollCancelsRef.current.add(registeredCancel);
    return registeredCancel;
  }, []);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useEffect(() => {
    const appointmentPollCancels = appointmentPollCancelsRef.current;
    return () => {
      for (const cancel of appointmentPollCancels) {
        cancel();
      }
      appointmentPollCancels.clear();
      exactAwaitJobIdRef.current = null;
      exactAwaitTakeGenerationRef.current = null;
      autoExportArmRef.current = null;
    };
  }, [widgetContext.context.appointment?.id]);

  useEffect(() => {
    latestSelectedTemplateIdRef.current = sessionModel.selectedTemplateId;
  }, [sessionModel.selectedTemplateId]);

  useEffect(
    () => () => {
      exactAwaitJobIdRef.current = null;
      autoExportArmRef.current = null;
      exactAwaitTakeGenerationRef.current = null;
    },
    [sessionModel.sessionId]
  );

  const [showConfetti, setShowConfetti] = useState(false);

  const { trackEvent } = useUserpilotTracking();
  const { performAutoExport } = useAutoExportDocument();

  const maybeAutoExportRenderedNote = useCallback(
    (
      sessionId: string,
      documents: SessionWithDocuments['documents'] | undefined
    ) => {
      const arm = autoExportArmRef.current;
      if (
        !arm ||
        arm.sessionId !== sessionId ||
        arm.exported ||
        !isCurrentAppointment(arm.appointmentId)
      ) {
        return;
      }
      if (
        !getAutoExportConfigFromContext(widgetContext.context)
          ?.autoExportAfterGeneration
      ) {
        return;
      }
      const note = processDocumentData(documents || []).documents[0];
      if (!note) return;
      arm.exported = true;
      performAutoExport(createDocumentState(note));
    },
    [isCurrentAppointment, performAutoExport, widgetContext.context]
  );

  const requestGeneratedNoteTab = useCallback(() => {
    const autoExportConfig = getAutoExportConfigFromContext(
      widgetContext.context
    );

    if (autoExportConfig?.autoExportAfterGeneration) {
      if (!autoExportConfig.skipDocumentTabNavigation) {
        const hideSoapNotesTab = autoExportConfig.hideSoapNotesTab === true;
        tabChangeHandler(
          hideSoapNotesTab ? TAB_IDS.transcript : TAB_IDS.document
        );
      }
    } else {
      tabChangeHandler(TAB_IDS.document);
    }
  }, [tabChangeHandler, widgetContext.context]);

  const generateDocumentBase = useCallback(
    async (options?: {
      templateId?: string;
      transcript?: string;
      clearExistingDocuments?: boolean;
      signal?: AbortSignal;
    }) => {
      const appointmentId = widgetContext.context.appointment?.id || '';
      if (!isCurrentAppointment(appointmentId)) {
        return null;
      }
      const { templateId, transcript, clearExistingDocuments, signal } =
        options || {};

      const selectedTemplate = sessionModel.templates.find(
        (t) => t.id === (templateId || sessionModel.selectedTemplateId)
      );

      const transcriptText =
        transcript ||
        TranscriptProcessor.toPlainTextWithOverrides(
          sessionModel.transcript,
          sessionModel.editOverrides
        );

      const {
        unstructured: unstructuredContext,
        structured: structuredContext,
      } = buildContextForPipeline(
        sessionModel.contextData,
        selectedTemplate,
        widgetContext?.context?.features
      );

      if (!selectedTemplate) {
        logError(
          'AppController',
          'Cannot generate document: template not found'
        );
        return null;
      }

      if (!transcriptText) {
        logError(
          'AppController',
          'Cannot generate document: no transcript available'
        );
        return null;
      }

      const backgroundGenerationEnabled = isFeatureEnabled(
        widgetContext.context?.features,
        FEATURE_FLAGS.SCRIBE_BACKGROUND_GENERATION
      );
      const unifiedSessionIdEnabled = isFeatureEnabled(
        widgetContext.context?.features,
        FEATURE_FLAGS.UNIFIED_SESSION_ID
      );

      try {
        setSessionModel((prev) => ({
          ...prev,
          isGenerating: true,
          generationError: false,
          bgJobPending: false,
        }));

        emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_STARTED, {
          sessionId: sessionModel.sessionId,
        });

        const templatePayload = buildTemplatePayload(
          selectedTemplate,
          templateId || sessionModel.selectedTemplateId || selectedTemplate.id
        );

        const generatedDocument =
          await documentTemplatesClient.generateDocuments({
            sessionId: sessionModel.sessionId,
            template: templatePayload,
            transcript: transcriptText,
            backgroundGeneration: backgroundGenerationEnabled,
            unifiedSessionId: unifiedSessionIdEnabled,
            features: featureFlagStates(widgetContext.context?.features),
            widgetConfig: buildWidgetConfigFlags(widgetContext.context),
            contextItems: buildContextItems(
              sessionModel.contextData,
              selectedTemplate
            ),
            context: {
              ...(unstructuredContext && { unstructured: unstructuredContext }),
              ...(structuredContext && { structured: structuredContext }),
            },
            ...(signal && { signal }),
          });

        if (!isCurrentAppointment(appointmentId)) {
          return null;
        }

        if (backgroundGenerationEnabled && generatedDocument?.jobId) {
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_STARTED, {
            sessionId: sessionModel.sessionId,
            job_id: generatedDocument.jobId,
          });
        }

        if (generatedDocument?.status === 'enqueued') {
          // The 'enqueued' response shape comes from the server. Only arm the
          // background-job spinner + WS subscription when the WIDGET flag is
          // also ON. In a server-ON / widget-OFF rollout-drift state,
          // subscribeForLiveUpdate would no-op, so arming bgJobPending here
          // would strand a permanent 'Generating' spinner. When the flag is
          // OFF, clear the optimistic spinner (isGenerating was set true before
          // the request) and do not subscribe.
          if (
            isFeatureEnabled(
              widgetContext.context?.features,
              FEATURE_FLAGS.SCRIBE_BACKGROUND_GENERATION
            )
          ) {
            autoExportArmRef.current = {
              sessionId: sessionModel.sessionId,
              appointmentId,
              exported: false,
            };
            setSessionModel((prev) => ({
              ...prev,
              isGenerating: false,
              bgJobPending: true,
              generationPhase: 'generating',
            }));
            await subscribeForLiveUpdate(
              sessionModel.sessionId,
              undefined,
              appointmentId
            );
          } else {
            setSessionModel((prev) => ({
              ...prev,
              isGenerating: false,
              bgJobPending: false,
              generationPhase: null,
            }));
          }
          return null;
        }

        if (generatedDocument) {
          const userId = auth.user?.username || auth.user?.email;
          if (userId) {
            const userPreferences = loadUserSettingsPreferences(userId);

            if (!userPreferences.hasSeenConfetti) {
              setShowConfetti(true);
              saveUserSettingsPreferences(userId, {
                hasSeenConfetti: true,
              });
            }
          }

          setSessionModel((prev) => {
            let updatedDocuments: typeof prev.documents;

            if (clearExistingDocuments) {
              updatedDocuments = [generatedDocument];
            } else {
              const filteredDocs = prev.documents.filter(
                (doc) => doc.templateId !== generatedDocument.templateId
              );

              updatedDocuments = [generatedDocument, ...filteredDocs].sort(
                (a, b) => {
                  return (
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                  );
                }
              );
            }

            return {
              ...prev,
              documents: updatedDocuments,
              isGenerating: false,
              generationError: false,
              bgJobPending: false,
              generationPhase: null,
            };
          });

          return generatedDocument;
        } else {
          throw new Error('No documents returned from API');
        }
      } catch (error) {
        if (!isCurrentAppointment(appointmentId)) {
          logDebug(
            'AppController',
            'Document generation error dropped: appointment changed',
            { error }
          );
          return null;
        }
        logError('AppController', 'Document generation failed:', {
          error,
        });

        setSessionModel((prev) => ({
          ...prev,
          isGenerating: false,
          generationError: true,
          bgJobPending: false,
          generationPhase: null,
        }));

        emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_ERROR, {
          sessionId: sessionModel.sessionId,
        });
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sessionModel.sessionId,
      sessionModel.transcript,
      sessionModel.editOverrides,
      sessionModel.selectedTemplateId,
      sessionModel.templates,
      sessionModel.contextData,
      widgetContext?.context,
      emitToHost,
      isCurrentAppointment,
    ]
  );

  const generateDocument = useDocumentGeneration(generateDocumentBase);

  const snapshotSelectedTemplate = useCallback(
    async (
      sessionId: string,
      template?: FormattedTemplate,
      templateId?: string
    ) => {
      if (!sessionId) return;
      const takeGeneration = audioRecorder.getTakeGeneration();
      if (takeGeneration === null) {
        throw new Error('Cannot snapshot template before a take begins');
      }
      // While this take is recording/awaiting its exact job, ignore generic
      // session-level completion pushes. A prior take may still finish on the
      // same entity; the exact-job poll below is the authority for this take.
      exactAwaitJobIdRef.current = 'PENDING_CURRENT_TAKE';
      exactAwaitTakeGenerationRef.current = takeGeneration;
      const effectiveTemplateId =
        templateId ??
        (latestSelectedTemplateIdRef.current ||
          sessionModel.selectedTemplateId);
      const selectedTemplate =
        template ??
        sessionModel.templates.find(
          (candidate) => candidate.id === effectiveTemplateId
        );
      if (!selectedTemplate) {
        throw new Error('Cannot snapshot template: template not found');
      }
      snapshotVersionRef.current = Math.max(
        snapshotVersionRef.current + 1,
        Date.now()
      );
      // In the unified flow the widget never sends a generate request, so the
      // snapshot is the only carrier of Context-tab data into server-side
      // generation. Build it with the same pipeline formatting the legacy
      // generate call uses so both paths produce identical context.
      const { unstructured, structured } = buildContextForPipeline(
        sessionModel.contextData,
        selectedTemplate,
        widgetContext?.context?.features
      );
      const context = {
        ...(unstructured && { unstructured }),
        ...(structured && { structured }),
      };
      const features = featureFlagStates(widgetContext?.context?.features);
      try {
        await sessionsClient.putTemplateSnapshot(sessionId, {
          template: buildTemplatePayload(selectedTemplate, effectiveTemplateId),
          takeGeneration,
          snapshotVersion: snapshotVersionRef.current,
          ...(Object.keys(context).length > 0 && { context }),
          ...(features && Object.keys(features).length > 0 && { features }),
          widgetConfig: buildWidgetConfigFlags(widgetContext?.context),
          contextItems: buildContextItems(
            sessionModel.contextData,
            selectedTemplate
          ),
        });
      } catch (error) {
        // A newer take has already cleared/replaced this pointer. The rejected
        // old request is harmless and must not surface an error in the new take.
        if (audioRecorder.getTakeGeneration() !== takeGeneration) return;
        // Same take: the snapshot never landed, so the exact-job poll that would
        // otherwise clear this sentinel never runs. Release it here or it strands
        // onDocumentGenerated, silently dropping every WS push for the rest of
        // this recording.
        if (exactAwaitJobIdRef.current === 'PENDING_CURRENT_TAKE') {
          exactAwaitJobIdRef.current = null;
          exactAwaitTakeGenerationRef.current = null;
        }
        throw error;
      }
    },
    [
      sessionModel.templates,
      sessionModel.selectedTemplateId,
      sessionModel.contextData,
      widgetContext?.context,
    ]
  );

  const selectTemplate = useCallback(
    async (templateId: string) => {
      const nextTemplate = sessionModel.templates.find(
        (t) => t.id === templateId
      );
      if (!nextTemplate || nextTemplate.isDisabled) {
        return;
      }

      const selectionRequestId = ++latestTemplateSelectionRequestRef.current;
      const previousTemplateId = sessionModel.selectedTemplateId;
      latestSelectedTemplateIdRef.current = templateId;

      setSessionModel((prev) => ({
        ...prev,
        selectedTemplateId: templateId,
      }));

      const seenNewTemplateIds = sessionModel.templates
        .filter((t) => !t.isHostProvided)
        .map((t) => t.id);

      const didPersistTemplateSelection = saveUserSettingsPreferences(
        auth.user.username,
        {
          lastTemplateSelected: templateId,
          ...(seenNewTemplateIds.length > 0 && { seenNewTemplateIds }),
        }
      );

      if (!didPersistTemplateSelection) {
        logWarn(
          'AppController',
          'Failed to persist last template selection to user preferences'
        );
      }

      trackEvent(TRACK_EVENTS.TEMPLATE_SELECTED, {
        templateId,
        templateName: sessionModel.templates.find((t) => t.id === templateId)
          ?.name,
        previousTemplateId: previousTemplateId || undefined,
        previousTemplateName: sessionModel.templates.find(
          (t) => t.id === previousTemplateId
        )?.name,
        changed: previousTemplateId !== templateId,
      });

      if (
        isCurrentlyRecording &&
        unifiedSessionIdEnabled &&
        sessionModel.sessionId
      ) {
        try {
          await snapshotSelectedTemplate(
            sessionModel.sessionId,
            nextTemplate,
            templateId
          );
        } catch (error) {
          // A newer selection owns the UI and a higher snapshotVersion. Its
          // server pointer cannot be overwritten by this request, so this
          // stale completion must not revert the newer picker state.
          if (
            latestTemplateSelectionRequestRef.current !== selectionRequestId
          ) {
            return;
          }
          logError(
            'AppController',
            'Failed to persist mid-recording template snapshot:',
            error
          );
          latestSelectedTemplateIdRef.current = previousTemplateId;
          setSessionModel((prev) => ({
            ...prev,
            selectedTemplateId: previousTemplateId,
            generationError: true,
          }));
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_ERROR, {
            sessionId: sessionModel.sessionId,
          });
          return;
        }
      }
      const documentExists = sessionModel.documents.some(
        (doc) => doc.templateId === templateId
      );

      if (
        isCurrentlyRecording ||
        !sessionModel.transcript.length ||
        documentExists
      ) {
        return;
      }

      await generateDocument({ templateId });
    },
    [
      generateDocument,
      isCurrentlyRecording,
      sessionModel.selectedTemplateId,
      sessionModel.templates,
      trackEvent,
      sessionModel.transcript,
      sessionModel.documents,
      sessionModel.sessionId,
      unifiedSessionIdEnabled,
      auth.user?.username,
      snapshotSelectedTemplate,
      emitToHost,
    ]
  );

  useEffect(() => {
    if (templates && templates.length > 0) {
      const userId = auth.user?.username;
      const preferences = loadUserSettingsPreferences(userId);

      let resolvedTemplateId: string | undefined;

      setSessionModel((prev) => {
        const incomingIds = new Set(templates.map((t) => t.id));
        const hostTemplates = prev.templates.filter(
          (t) => t.isHostProvided && !incomingIds.has(t.id)
        );
        const allTemplates = [...templates, ...hostTemplates];

        const selectedTemplateId = getDefaultTemplateId(
          allTemplates,
          preferences.lastTemplateSelected,
          preferences.seenNewTemplateIds
        );

        resolvedTemplateId = selectedTemplateId;

        return {
          ...prev,
          templates: allTemplates,
          selectedTemplateId,
        };
      });

      if (
        resolvedTemplateId &&
        preferences.lastTemplateSelected &&
        resolvedTemplateId !== preferences.lastTemplateSelected
      ) {
        saveUserSettingsPreferences(userId, {
          lastTemplateSelected: resolvedTemplateId,
        });
      }

      const fetchedIds = templates
        .filter((t) => !t.isHostProvided)
        .map((t) => t.id);
      if (fetchedIds.length > 0) {
        const merged = Array.from(
          new Set([...(preferences.seenNewTemplateIds ?? []), ...fetchedIds])
        );
        saveUserSettingsPreferences(userId, { seenNewTemplateIds: merged });
      }
    } else if (templates && templates.length === 0) {
      setSessionModel((prev) => {
        // When no API templates, still preserve host templates
        const hostTemplates = prev.templates.filter((t) => t.isHostProvided);

        if (prev.selectedTemplateId && hostTemplates.length === 0) {
          return {
            ...prev,
            templates: [],
            selectedTemplateId: '',
          };
        }
        return {
          ...prev,
          templates: hostTemplates,
        };
      });
    }
  }, [templates, auth.user]);

  const applySessionDocuments = useCallback(
    (
      sessionWithDocuments: SessionWithDocuments,
      expectedAppointmentId: string,
      origin: SessionDocumentsOrigin
    ) => {
      const sessionId =
        sessionWithDocuments.session.id ||
        sessionWithDocuments.session.sessionId ||
        '';

      if (!isCurrentAppointment(expectedAppointmentId)) {
        return;
      }

      const processedData = processSessionData(
        sessionId,
        sessionWithDocuments.documents || []
      );

      const hasUsableData =
        processedData.success ||
        processedData.documents.length > 0 ||
        processedData.transcriptLines.length > 0;
      const hydratedLastTake =
        sessionWithDocuments.lastTake !== undefined
          ? sessionWithDocuments.lastTake
          : sessionWithDocuments.session.lastTake;
      const activeRecorder =
        sessionWithDocuments.activeRecorder ??
        sessionWithDocuments.session.activeRecorder;
      const recordingPaused =
        sessionWithDocuments.recordingPaused ??
        sessionWithDocuments.session.recordingPaused;
      const currentDeviceId = readOrCreatePersistedRecordingDeviceId();
      const activeRecorderOnOtherDevice =
        activeRecorder &&
        (!activeRecorder.deviceId ||
          !currentDeviceId ||
          activeRecorder.deviceId !== currentDeviceId)
          ? activeRecorder
          : null;

      if (
        widgetContext.context.appointment?.sessionId === sessionId &&
        widgetContext.context.appointment?.id
      ) {
        hydratedSessionRef.current = {
          appointmentId: widgetContext.context.appointment.id,
          sessionId,
        };
      }

      // Set this before the usable-data check: a live session can have no
      // documents yet, but the user still needs to know it is in use. Keep the
      // unified resume metadata in the same update so this early warning
      // render cannot race the resume-confirmation gate's lastTake state.
      setSessionModel((prev) => ({
        ...prev,
        ...(includeLastTakeInHydration && { sessionId }),
        ...(includeLastTakeInHydration &&
          hydratedLastTake !== undefined && {
            lastTake: hydratedLastTake,
          }),
        activeRecorder: activeRecorderOnOtherDevice,
        ...(recordingPaused !== undefined && { recordingPaused }),
      }));

      if (hasUsableData) {
        // A background completion can land after useEncounterHandler cleared
        // the store for another patient; adopting its session there would
        // re-bind the previous patient's session.
        if (origin === 'hydration' && includeLastTakeInHydration && sessionId) {
          const recordingState = useRecordingStore.getState();
          if (
            !recordingState.isRecording &&
            !recordingState.recordingSessionId
          ) {
            recordingState.setRecordingSessionId(sessionId);
          }
        }

        if (!processedData.success) {
          logError(
            'AppController',
            'Partial session data loaded (transcript parse failed):',
            processedData.error
          );
        }

        trackEvent(TRACK_EVENTS.EXISTING_SESSION_VIEWED, {
          sessionId,
          encounterId:
            sessionWithDocuments.session.id ||
            sessionWithDocuments.session.sessionId,
          hasTranscript: processedData.transcriptLines.length > 0,
          hasDocument: processedData.documents.length > 0,
          transcriptSegmentCount: processedData.transcriptLines.length,
          documentCount: processedData.documents.length,
        });

        setSessionModel((prev) => ({
          ...prev,
          ...(includeLastTakeInHydration && { sessionId }),
          transcript: processedData.transcriptLines,
          documents: processedData.documents,
          isGenerating: false,
          bgJobPending: false,
          generationError: false,
          generationPhase: null,
          selectedTemplateId:
            processedData.documents[0]?.templateId || prev.selectedTemplateId,
          transcriptDocumentId: processedData.transcriptDocumentId,
          transcriptIsEdited: processedData.transcriptIsEdited,
          transcriptUpdatedAt: processedData.transcriptUpdatedAt,
          transcriptUpdatedBy: processedData.transcriptUpdatedBy,
          editOverrides: processedData.editOverrides,
          ...(hydratedLastTake !== undefined && {
            lastTake: hydratedLastTake,
          }),
          activeRecorder: activeRecorderOnOtherDevice,
          ...(recordingPaused !== undefined && { recordingPaused }),
        }));

        expand();
      } else {
        logError(
          'AppController',
          'Failed to process session data:',
          processedData.error
        );
      }
    },
    [
      expand,
      includeLastTakeInHydration,
      isCurrentAppointment,
      trackEvent,
      widgetContext.context.appointment?.id,
      widgetContext.context.appointment?.sessionId,
    ]
  );

  // Shared COMPLETED handling: render the note from the read, or re-fetch once
  // for eventual consistency, falling back to best-available / spinner. Used by
  // both the reopen COMPLETED branch and the post-subscribe reconcile.
  const applyCompletedJobStatus = async (
    sessionId: string,
    sessionWithDocuments: SessionWithDocuments | null | undefined,
    hasGeneratedNote: boolean,
    expectedAppointmentId: string,
    origin: SessionDocumentsOrigin
  ) => {
    if (!isCurrentAppointment(expectedAppointmentId)) {
      return undefined;
    }
    if (sessionWithDocuments?.session && hasGeneratedNote) {
      applySessionDocuments(
        sessionWithDocuments,
        expectedAppointmentId,
        origin
      );
      return sessionWithDocuments;
    }
    // Note wasn't in the first read (eventual consistency): re-fetch once
    // and apply if it has landed.
    const refreshed = await sessionsClient.getSessionWithDocuments(sessionId, {
      includeLastTake: includeLastTakeInHydration,
    });
    if (!isCurrentAppointment(expectedAppointmentId)) {
      return undefined;
    }
    const refreshedHasNote = (refreshed?.documents || []).some(
      (doc) => doc.type !== 'transcript' && doc.documentType !== 'transcript'
    );
    if (refreshed?.session && refreshedHasNote) {
      applySessionDocuments(refreshed, expectedAppointmentId, origin);
      return refreshed;
    }
    // Still absent but a prior note exists: render best-available; the WS
    // push will replace it.
    if (hasGeneratedNote && sessionWithDocuments?.session) {
      applySessionDocuments(
        sessionWithDocuments,
        expectedAppointmentId,
        origin
      );
      return sessionWithDocuments;
    }
    // Still absent and no prior note: keep the spinner until the WS push
    // lands.
    setSessionModel((prev) => ({
      ...prev,
      isGenerating: false,
      bgJobPending: true,
    }));
    return null;
  };

  const armBackgroundNoteMachinery = async (
    durableId: string,
    hooks:
      | {
          onStatus?: (status: string | null) => void;
          onTerminal?: () => void;
          onFailed?: () => void;
          onExpired?: () => void;
          onNoteRendered?: (
            sessionWithDocuments: SessionWithDocuments | null | undefined
          ) => void;
        }
      | undefined,
    exactJobId: string | undefined,
    expectedAppointmentId: string
  ) => {
    if (!isCurrentAppointment(expectedAppointmentId)) return;
    const getJobStatus = () =>
      exactJobId
        ? documentTemplatesClient.getScribeJobStatus(exactJobId)
        : documentTemplatesClient.getScribeJobStatusBySession(durableId);
    // TEMP (NEB-98752): fire-and-forget poll fallback for when the WS
    // completion push finds no live connection. Started FIRST — independent of
    // connect() — so that even if connect() throws (the exact WS-broken
    // scenario this poll covers), the fallback still runs and resolves the
    // spinner. Do NOT await — this must return promptly so the generate promise
    // resolves and useDocumentGeneration's timeout logic is unaffected. Remove
    // once WS delivery is reliable.
    let cancelRegisteredPoll = () => {};
    const releasePoll = () => cancelRegisteredPoll();
    const cancelPoll = startScribeJobPollFallback(durableId, {
      getJobStatus: async () => {
        if (!isCurrentAppointment(expectedAppointmentId)) {
          return null;
        }
        const status = await getJobStatus();
        if (!isCurrentAppointment(expectedAppointmentId)) {
          return null;
        }
        hooks?.onStatus?.(status);
        return status;
      },
      onCompleted: async () => {
        try {
          if (!isCurrentAppointment(expectedAppointmentId)) return;
          hooks?.onTerminal?.();
          const sessionWithDocuments =
            await sessionsClient.getSessionWithDocuments(durableId, {
              includeLastTake: includeLastTakeInHydration,
            });
          if (!isCurrentAppointment(expectedAppointmentId)) return;
          const hasGeneratedNote = (sessionWithDocuments?.documents || []).some(
            (doc) =>
              doc.type !== 'transcript' && doc.documentType !== 'transcript'
          );
          const rendered = await applyCompletedJobStatus(
            durableId,
            sessionWithDocuments,
            hasGeneratedNote,
            expectedAppointmentId,
            'background'
          );
          if (
            rendered === undefined ||
            !isCurrentAppointment(expectedAppointmentId)
          ) {
            return;
          }
          if (rendered === null) {
            // NEB-100535: COMPLETED but no note found — treat as failure rather than
            // waiting indefinitely for a WS push that may never come.
            setSessionModel((prev) => ({
              ...prev,
              isGenerating: false,
              bgJobPending: false,
              generationError: true,
              generationPhase: null,
            }));
          }
          if (rendered !== null) {
            maybeAutoExportRenderedNote(durableId, rendered.documents);
          }
          hooks?.onNoteRendered?.(rendered);
        } finally {
          releasePoll();
        }
      },
      onFailed: () => {
        if (!isCurrentAppointment(expectedAppointmentId)) {
          releasePoll();
          return;
        }
        releasePoll();
        hooks?.onTerminal?.();
        hooks?.onFailed?.();
        setSessionModel((prev) => ({
          ...prev,
          isGenerating: false,
          bgJobPending: false,
          generationError: true,
          generationPhase: null,
        }));
        if (unifiedSessionIdEnabled) {
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_ERROR, {
            sessionId: durableId,
          });
        }
      },
      onExpired: () => {
        if (isCurrentAppointment(expectedAppointmentId)) {
          hooks?.onExpired?.();
        }
        releasePoll();
      },
    });
    cancelRegisteredPoll = registerAppointmentPollCancel(cancelPoll);

    try {
      if (!isCurrentAppointment(expectedAppointmentId)) return;
      const client = WhisperClient.getInstance();
      // connect() is idempotent — reuse the same instance that already has the
      // onDocumentGenerated/onTranscript handlers set.
      await client.connect();
      if (!isCurrentAppointment(expectedAppointmentId)) return;
      client.subscribe(durableId);

      // Single post-subscribe reconcile: if the job already COMPLETED, run the
      // existing completed re-fetch path. Exactly once, no loop.
      const reconcileStatus = await getJobStatus();
      if (!isCurrentAppointment(expectedAppointmentId)) return;
      hooks?.onStatus?.(reconcileStatus);
      if (reconcileStatus === 'COMPLETED') {
        hooks?.onTerminal?.();
        const sessionWithDocuments =
          await sessionsClient.getSessionWithDocuments(durableId, {
            includeLastTake: includeLastTakeInHydration,
          });
        if (!isCurrentAppointment(expectedAppointmentId)) return;
        const hasGeneratedNote = (sessionWithDocuments?.documents || []).some(
          (doc) =>
            doc.type !== 'transcript' && doc.documentType !== 'transcript'
        );
        const rendered = await applyCompletedJobStatus(
          durableId,
          sessionWithDocuments,
          hasGeneratedNote,
          expectedAppointmentId,
          'background'
        );
        if (
          rendered === undefined ||
          !isCurrentAppointment(expectedAppointmentId)
        ) {
          return;
        }
        // Released only after the fetch: if it throws, the poll stays as the
        // fallback.
        releasePoll();
        if (rendered === null) {
          // NEB-100535: COMPLETED but no note found — treat as failure rather than
          // waiting indefinitely for a WS push that may never come.
          setSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            bgJobPending: false,
            generationError: true,
            generationPhase: null,
          }));
        }
        if (rendered !== null) {
          maybeAutoExportRenderedNote(durableId, rendered.documents);
        }
        hooks?.onNoteRendered?.(rendered);
      }
    } catch (error) {
      if (isCurrentAppointment(expectedAppointmentId)) {
        logError('AppController', 'armBackgroundNoteMachinery failed:', error);
      } else {
        logDebug(
          'AppController',
          'armBackgroundNoteMachinery error dropped: appointment changed',
          error
        );
      }
    }
  };

  const subscribeForLiveUpdate = async (
    durableId: string,
    onNoteRendered:
      | ((
          sessionWithDocuments: SessionWithDocuments | null | undefined
        ) => void)
      | undefined,
    expectedAppointmentId: string
  ) => {
    if (
      !backgroundRecoveryEnabled ||
      !isCurrentAppointment(expectedAppointmentId)
    ) {
      return;
    }
    await armBackgroundNoteMachinery(
      durableId,
      {
        onExpired: () => {
          if (!isCurrentAppointment(expectedAppointmentId)) return;
          setSessionModel((prev) => ({
            ...prev,
            bgJobPending: false,
            generationError: true,
            generationPhase: null,
          }));
        },
        onNoteRendered,
      },
      undefined,
      expectedAppointmentId
    );
  };

  /**
   * Resolves once background-note polling is armed; the boolean is true only if
   * the note was already COMPLETED at that reconcile — a later render happens
   * asynchronously via poll/WS.
   */
  const awaitBackgroundNote = async (sessionId: string) => {
    const appointmentId = widgetContext.context.appointment?.id || '';
    if (
      !unifiedSessionIdEnabled ||
      !sessionId ||
      !isCurrentAppointment(appointmentId)
    ) {
      return false;
    }
    let stopNoteRendered = false;
    autoExportArmRef.current = { sessionId, appointmentId, exported: false };

    const handleRenderedStopNote = (
      sessionWithDocuments: SessionWithDocuments | null | undefined
    ) => {
      if (!isCurrentAppointment(appointmentId)) return null;
      const note = processDocumentData(sessionWithDocuments?.documents || [])
        .documents[0];
      if (!note) return null;

      stopNoteRendered = true;
      requestGeneratedNoteTab();
      return note;
    };

    const takeGeneration = audioRecorder.getTakeGeneration();
    if (takeGeneration === null) {
      // NEB-100535: no local take on this device — e.g. a session recorded on
      // another device is being stopped/resumed here. The take-scoped
      // docgen-request poll below cannot arm without a take, so guarantee the
      // session-scoped background poll instead: it renders a COMPLETED note by
      // session id and self-terminates at the 120s cap (generationError if no
      // note ever lands). This does NOT reintroduce NEB-100347 — it polls the
      // session's job status, never a phantom take's docgen request. Reached
      // only with background generation on (subscribeForLiveUpdate self-gates
      // on the flag).
      setSessionModel((prev) => ({
        ...prev,
        isGenerating: false,
        bgJobPending: true,
        generationError: false,
        generationPhase: 'generating',
      }));
      await subscribeForLiveUpdate(
        sessionId,
        handleRenderedStopNote,
        appointmentId
      );
      return stopNoteRendered;
    }

    // No audio reached the server for this take, so no recording-complete was
    // (or will be) sent and the server will never generate a note. Land the
    // idle SOAP tab (no speech captured) instead of entering a generating
    // spinner that could never resolve (NEB-100535).
    if (!audioRecorder.hasUploadedAudioChunk()) {
      setSessionModel((prev) => ({
        ...prev,
        isGenerating: false,
        bgJobPending: false,
        generationError: false,
        generationPhase: null,
      }));
      return false;
    }

    setSessionModel((prev) => ({
      ...prev,
      isGenerating: false,
      bgJobPending: true,
      generationError: false,
      generationPhase: 'generating',
    }));

    const requestPollStartedAt = Date.now();
    let requestPollIntervalMs = DOCGEN_REQUEST_POLL_INITIAL_INTERVAL_MS;
    let requestPollTimer: number | undefined;
    const cancelRequestPoll = registerAppointmentPollCancel(() => {
      if (requestPollTimer !== undefined) {
        window.clearTimeout(requestPollTimer);
        requestPollTimer = undefined;
      }
    });
    const finishRequestPoll = () => cancelRequestPoll();

    const failCurrentTakeRequest = () => {
      if (!isCurrentAppointment(appointmentId)) return;
      finishRequestPoll();
      setSessionModel((prev) => ({
        ...prev,
        isGenerating: false,
        bgJobPending: false,
        generationError: true,
        generationPhase: null,
      }));
      if (exactAwaitTakeGenerationRef.current === takeGeneration) {
        exactAwaitJobIdRef.current = null;
        exactAwaitTakeGenerationRef.current = null;
      }
    };

    const pollForCurrentTakeRequest = async (): Promise<boolean> => {
      if (!isCurrentAppointment(appointmentId)) {
        finishRequestPoll();
        return false;
      }
      if (audioRecorder.getTakeGeneration() !== takeGeneration) {
        finishRequestPoll();
        if (exactAwaitTakeGenerationRef.current === takeGeneration) {
          exactAwaitJobIdRef.current = null;
          exactAwaitTakeGenerationRef.current = null;
        }
        return false;
      }
      try {
        const request = await sessionsClient.getStitchDocGenRequest(
          sessionId,
          takeGeneration
        );
        if (!isCurrentAppointment(appointmentId)) {
          logDebug(
            'AppController',
            'Docgen request result dropped: appointment changed',
            { status: request.status, errorCode: request.errorCode }
          );
          finishRequestPoll();
          return false;
        }
        if (request.status === 'FAILED') {
          logError(
            'AppController',
            `Document generation request failed: ${request.errorCode ?? 'UNKNOWN'}`
          );
          failCurrentTakeRequest();
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_ERROR, {
            sessionId,
          });
          return false;
        }
        if (request.status === 'ENQUEUED' && request.jobId) {
          finishRequestPoll();
          if (audioRecorder.getTakeGeneration() !== takeGeneration)
            return false;
          exactAwaitJobIdRef.current = request.jobId;
          exactAwaitTakeGenerationRef.current = takeGeneration;
          setSessionModel((prev) => ({
            ...prev,
            bgJobPending: true,
            generationError: false,
            generationPhase: 'generating',
          }));

          void sessionsClient
            .getSessionWithDocuments(sessionId, {
              includeLastTake: includeLastTakeInHydration,
            })
            .then((sessionWithDocuments) => {
              if (!isCurrentAppointment(appointmentId)) return;
              const transcriptDoc = (
                sessionWithDocuments?.documents || []
              ).find(
                (doc) =>
                  doc.type === 'transcript' || doc.documentType === 'transcript'
              );
              if (transcriptDoc?.id) {
                setSessionModel((prev) => ({
                  ...prev,
                  transcriptDocumentId: transcriptDoc.id,
                }));
              }
            })
            .catch((error) => {
              if (isCurrentAppointment(appointmentId)) {
                logError(
                  'AppController',
                  'Failed to hydrate transcript document id:',
                  error
                );
              } else {
                logDebug(
                  'AppController',
                  'Transcript document id error dropped: appointment changed',
                  error
                );
              }
            });

          await armBackgroundNoteMachinery(
            sessionId,
            {
              onFailed: () => {
                if (exactAwaitTakeGenerationRef.current === takeGeneration) {
                  exactAwaitJobIdRef.current = null;
                  exactAwaitTakeGenerationRef.current = null;
                }
              },
              // The exact-job poll is the only renderer while the exact guard
              // is armed. When it expires without a terminal status, degrade
              // the guard so a completion push that arrives after the cap
              // still renders through the session-id-matched handler.
              onExpired: () => {
                if (
                  isCurrentAppointment(appointmentId) &&
                  exactAwaitTakeGenerationRef.current === takeGeneration
                ) {
                  exactAwaitJobIdRef.current = null;
                  exactAwaitTakeGenerationRef.current = null;
                  setSessionModel((prev) => ({
                    ...prev,
                    bgJobPending: false,
                    generationPhase: null,
                  }));
                }
              },
              onNoteRendered: (sessionWithDocuments) => {
                if (
                  !isCurrentAppointment(appointmentId) ||
                  audioRecorder.getTakeGeneration() !== takeGeneration ||
                  exactAwaitTakeGenerationRef.current !== takeGeneration
                ) {
                  return;
                }
                exactAwaitJobIdRef.current = null;
                exactAwaitTakeGenerationRef.current = null;
                handleRenderedStopNote(sessionWithDocuments);
                maybeAutoExportRenderedNote(
                  sessionId,
                  sessionWithDocuments?.documents
                );
              },
            },
            request.jobId,
            appointmentId
          );
          return stopNoteRendered;
        }
      } catch (error) {
        if (isCurrentAppointment(appointmentId)) {
          logError('AppController', 'Failed to read docgen request:', error);
        } else {
          logDebug(
            'AppController',
            'Docgen request error dropped: appointment changed',
            error
          );
          finishRequestPoll();
          return false;
        }
      }

      const elapsedMs = Date.now() - requestPollStartedAt;
      if (elapsedMs < DOCGEN_REQUEST_POLL_MAX_MS) {
        const remainingMs = DOCGEN_REQUEST_POLL_MAX_MS - elapsedMs;
        const nextDelayMs = Math.min(requestPollIntervalMs, remainingMs);
        requestPollTimer = window.setTimeout(() => {
          requestPollTimer = undefined;
          void pollForCurrentTakeRequest();
        }, nextDelayMs);
        requestPollIntervalMs = Math.min(
          requestPollIntervalMs * 2,
          DOCGEN_REQUEST_POLL_MAX_INTERVAL_MS
        );
      } else {
        failCurrentTakeRequest();
      }
      return false;
    };

    return pollForCurrentTakeRequest();
  };

  const returnSessionData = async function (
    sessionId: string,
    appointmentId: string
  ) {
    if (!isCurrentAppointment(appointmentId)) return;
    try {
      const sessionWithDocuments = await sessionsClient.getSessionWithDocuments(
        sessionId,
        {
          includeLastTake: includeLastTakeInHydration,
        }
      );
      if (!isCurrentAppointment(appointmentId)) return;

      const hasGeneratedNote = (sessionWithDocuments?.documents || []).some(
        (doc) => doc.type !== 'transcript' && doc.documentType !== 'transcript'
      );

      if (sessionWithDocuments?.session) {
        // NEB-99489: hydrate the per-session recorded total from the persisted
        // session.duration (seconds) so a cross-reload reopen shows the real
        // length instead of 00:00. Keyed on sessionId (the duration grain), which
        // matches the activeRecordingSession.sessionId the display read uses. Both
        // the nested and flattened response shapes now carry the real duration.
        const persistedDurationSeconds = sessionWithDocuments.session.duration;
        if (persistedDurationSeconds > 0) {
          useRecordingStore
            .getState()
            .setCompletedDuration(
              sessionId,
              formatElapsedMs(persistedDurationSeconds * 1000)
            );
        }
      }

      // Flag OFF: behave exactly as returnSessionData did before the NEB-98752
      // background block was added — render whatever the read returned and clear
      // the optimistic spinner. No jobStatus poll, no connect/subscribe, no
      // bgJobPending writes beyond the baseline clear that applySessionDocuments
      // / the explicit clear perform.
      if (!backgroundRecoveryEnabled) {
        if (sessionWithDocuments?.session) {
          applySessionDocuments(
            sessionWithDocuments,
            appointmentId,
            'hydration'
          );
        }
        setSessionModel((prev) => ({
          ...prev,
          isGenerating: false,
          bgJobPending: false,
        }));
        return;
      }

      // Cold-reopen recovery: read the job status FIRST so the optimistic
      // spinner is reconciled against the ACTUAL job status (a
      // partial/unparseable session can never leave isGenerating stuck true),
      // then decide what to render. The WS push (onDocumentGenerated) delivers
      // the fresh note when ready.
      const jobStatus =
        await documentTemplatesClient.getScribeJobStatusBySession(sessionId);
      if (!isCurrentAppointment(appointmentId)) return;

      // A (re)generation is still in flight. NEB-100535: a note the read
      // already returned must NOT be blanked to a bare spinner — surfacing an
      // existing note always beats hiding it, so render it and keep subscribing
      // so the WS push replaces it when the fresh note lands. Only when there
      // is genuinely no note yet do we blank to the processing spinner.
      if (jobStatus === 'PENDING' || jobStatus === 'RUNNING') {
        if (hasGeneratedNote && sessionWithDocuments?.session) {
          applySessionDocuments(
            sessionWithDocuments,
            appointmentId,
            'hydration'
          );
        } else {
          if (sessionWithDocuments?.session) {
            applySessionDocuments(
              sessionWithDocuments,
              appointmentId,
              'hydration'
            );
          }
          // Intentional: hasGeneratedNote is derived from documents alone, so the
          // note-present-but-session-absent shape reaches here and must still set
          // bgJobPending. Do not flatten to `if (!hasGeneratedNote) setPending`.
          setSessionModel((prev) => ({
            ...prev,
            bgJobPending: true,
            isGenerating: false,
            generationError: false,
          }));
        }
        await subscribeForLiveUpdate(sessionId, undefined, appointmentId);
        return;
      }

      if (jobStatus === 'COMPLETED') {
        const result = await applyCompletedJobStatus(
          sessionId,
          sessionWithDocuments,
          hasGeneratedNote,
          appointmentId,
          'hydration'
        );
        if (result === undefined || !isCurrentAppointment(appointmentId)) {
          return;
        }
        if (result === null) {
          // NEB-100535: COMPLETED but no note found — treat as failure rather than
          // waiting indefinitely for a WS push that may never come.
          setSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            bgJobPending: false,
            generationError: true,
            generationPhase: null,
          }));
        }
        return;
      }

      if (
        jobStatus === 'FAILED' ||
        jobStatus === 'TIMED_OUT' ||
        jobStatus === 'CANCELLED'
      ) {
        const unifiedSessionIdEnabled = isFeatureEnabled(
          widgetContext.context?.features,
          FEATURE_FLAGS.UNIFIED_SESSION_ID
        );

        if (unifiedSessionIdEnabled && sessionWithDocuments?.session) {
          applySessionDocuments(
            sessionWithDocuments,
            appointmentId,
            'hydration'
          );
        }
        if (!isCurrentAppointment(appointmentId)) return;
        setSessionModel((prev) => ({
          ...prev,
          isGenerating: false,
          bgJobPending: false,
          generationError: true,
        }));
        if (unifiedSessionIdEnabled) {
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.GENERATION_ERROR, {
            sessionId,
          });
        }
        return;
      }

      // No job in flight (null): render whatever the read returned to preserve
      // transcript-only / partial-transcript behavior, then always clear the
      // optimistic spinner — applySessionDocuments only clears it when it has
      // usable data, so the partial/unparseable case (model untouched) still
      // needs the explicit clear.
      if (sessionWithDocuments?.session) {
        applySessionDocuments(sessionWithDocuments, appointmentId, 'hydration');
      }
      if (!isCurrentAppointment(appointmentId)) return;
      setSessionModel((prev) => ({
        ...prev,
        isGenerating: false,
        bgJobPending: false,
      }));
    } catch (error) {
      if (!isCurrentAppointment(appointmentId)) {
        logDebug(
          'AppController',
          'Auto-population error dropped: appointment changed',
          error
        );
        return;
      }
      logError('AppController', 'Auto-population error:', error);
      // NEB-100535: clear generating state on reopen failure, but only if
      // a generation was in progress — flag-off sessions never enter this state.
      setSessionModel((prev) => {
        if (prev.isGenerating || prev.bgJobPending) {
          return {
            ...prev,
            isGenerating: false,
            bgJobPending: false,
            generationError: true,
            generationPhase: null,
          };
        }
        return prev;
      });
    }
  };

  useEffect(
    () => {
      const handleAppointmentChange = async () => {
        const contextAppointmentId =
          widgetContext.context.appointment?.id || '';
        const contextSessionId =
          widgetContext.context.appointment?.sessionId || '';
        const isNewAppointment =
          contextAppointmentId !== sessionModel.appointmentId;
        if (isNewAppointment) {
          if (
            hydratedSessionRef.current?.appointmentId !==
              contextAppointmentId ||
            hydratedSessionRef.current?.sessionId !== contextSessionId
          ) {
            hydratedSessionRef.current = null;
          }
          // Context the host sent while this switch was parked during the prior take's stop.
          const { pendingEncounter, pendingContextData } =
            useRecordingStore.getState();
          const isParkedSwitch = pendingEncounter?.id === contextAppointmentId;
          const parkedContextData =
            isParkedSwitch &&
            pendingContextData?.appointmentId === contextAppointmentId
              ? pendingContextData.contextData
              : null;
          setSessionModel((prevSessionModel) => {
            if (
              hydratedSessionRef.current?.appointmentId ===
                contextAppointmentId &&
              hydratedSessionRef.current?.sessionId === contextSessionId
            ) {
              return prevSessionModel;
            }

            const currentSelectedId = prevSessionModel.selectedTemplateId;
            const hasValidSelection = prevSessionModel.templates.some(
              (t) => t.id === currentSelectedId && !t.isDisabled
            );
            const selectedTemplateId = hasValidSelection
              ? currentSelectedId
              : (prevSessionModel.templates.find((t) => !t.isDisabled)?.id ??
                '');

            // On initial load (no prior appointment) the host can emit
            // setEncounter + setContextData synchronously, so this reset commits
            // after both. Preserve the just-set contextData in that case, but
            // clear it on a real switch so a prior encounter's context can't leak.
            const isInitialLoad = !prevSessionModel.appointmentId;

            const resetModel = {
              ...INITIAL_SESSION_MODEL,
              appointmentId: contextAppointmentId,
              sessionId: contextSessionId,
              templates: prevSessionModel.templates,
              selectedTemplateId: selectedTemplateId,
              contextData:
                parkedContextData ??
                (isInitialLoad ? prevSessionModel.contextData : []),
            };

            return resetModel;
          });
          if (isParkedSwitch) {
            useRecordingStore.getState().setPendingEncounter(null);
            useRecordingStore.getState().setPendingContextData(null);
          }
        }

        // Hydrate whenever there is a context session that has NOT yet been
        // hydrated, rather than gating on isNewAppointment. On a fresh remount
        // the reset block above stamps sessionModel.appointmentId on the first
        // (auth-not-ready) render, so by the time auth flips true the
        // isNewAppointment check reads false and hydration would be skipped
        // (Flow 7 paused-close reopen). Keying on the hydratedSessionRef dedupe
        // guard means the auth-ready render always hydrates a not-yet-hydrated
        // session while still firing exactly once.
        const notYetHydrated =
          hydratedSessionRef.current?.sessionId !== contextSessionId ||
          hydratedSessionRef.current?.appointmentId !== contextAppointmentId;
        if (contextSessionId && auth.isAuthenticated && notYetHydrated) {
          setSessionModel((prev) => ({
            ...prev,
            isGenerating: true,
            bgJobPending: false,
          }));
          try {
            await returnSessionData(contextSessionId, contextAppointmentId);
            if (!isCurrentAppointment(contextAppointmentId)) {
              return;
            }
            // Stamp the dedupe guard for THIS flow too. returnSessionData's
            // flag-OFF path only stamps (via applySessionDocuments, L705) when
            // the read returned a session; stamping here as well guarantees the
            // gate dedupes on the next render even if that branch was skipped,
            // so the new notYetHydrated gate can never loop and hammer the read.
            hydratedSessionRef.current = {
              appointmentId: contextAppointmentId,
              sessionId: contextSessionId,
            };
          } catch (error) {
            logError(
              'AppController',
              'Failed to auto-populate session:',
              error
            );
          }
        }
      };

      handleAppointmentChange();
    },

    // exhaustive-deps is disabled to keep this list intentional. sessionId is
    // included on purpose: the host delivers it asynchronously on reopen (auth
    // already true, appointmentId unchanged), so it must retrigger hydration.
    // The host also saves the sessionId after a recording, which retriggers
    // this effect too, but the notYetHydrated gate (keyed on hydratedSessionRef,
    // stamped on hydration success) short-circuits that re-run so it cannot loop.

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      auth.isAuthenticated,
      widgetContext.context.appointment?.id,
      widgetContext.context.appointment?.sessionId,
    ]
  );

  useEffect(() => {
    if (!backgroundRecoveryEnabled) {
      return;
    }
    const client = WhisperClient.getInstance();
    const previousHandler = client.onDocumentGenerated;
    const appointmentId = activeAppointmentIdRef.current;

    client.onDocumentGenerated = (data: SessionWithDocuments) => {
      if (exactAwaitJobIdRef.current) {
        return;
      }
      const pushedSessionId =
        data?.session?.id ||
        data?.session?.sessionId ||
        (data as { sessionId?: string })?.sessionId ||
        '';

      // Guard: ignore a stale push for a different encounter.
      if (
        pushedSessionId &&
        (!isCurrentAppointment(appointmentId) ||
          pushedSessionId !== sessionModel.sessionId)
      ) {
        return;
      }

      const normalized: SessionWithDocuments = data.session
        ? data
        : ({
            session: {
              id: pushedSessionId,
              sessionId: pushedSessionId,
              patientName: '',
              visitDate: '',
              visitType: '',
              duration: 0,
              provider: '',
              transcript: [],
              documents: data.documents || [],
            },
            documents: data.documents || [],
            totalDocuments:
              (data as { totalCount?: number; totalDocuments?: number })
                .totalCount ??
              (data as { totalDocuments?: number }).totalDocuments ??
              data.documents?.length ??
              0,
            metadata: data.metadata,
          } as SessionWithDocuments);

      applySessionDocuments(normalized, appointmentId, 'background');
      maybeAutoExportRenderedNote(pushedSessionId, normalized.documents);
    };

    return () => {
      client.onDocumentGenerated = previousHandler;
    };
  }, [
    applySessionDocuments,
    backgroundRecoveryEnabled,
    isCurrentAppointment,
    maybeAutoExportRenderedNote,
    sessionModel.sessionId,
    widgetContext.context?.features,
    unifiedSessionIdEnabled,
  ]);

  useEffect(() => {
    if (healthStatus) {
      setSessionModel((prev) => ({
        ...prev,
        healthStatus: healthStatus,
      }));
    }
  }, [healthStatus]);

  const contextValue: AppSharedContext = {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    requireAuth: auth.requireAuth,
    tenantId: auth.tenantId,
    getValidatedToken: auth.getAccessToken,
    isTokenValid: async () => {
      const token = await auth.getAccessToken();
      return !!token;
    },

    contestStatus,
    contestEntries,

    sessionModel,
    selectTemplate,
    generateDocument,
    snapshotSelectedTemplate,
    awaitBackgroundNote,
    updateSessionModel: setSessionModel,
  };

  return (
    <AppContext.Provider value={contextValue}>
      <EventHandler />
      <AppLayout
        isAuthenticated={auth.isAuthenticated}
        requireAuth={auth.requireAuth}
        handleLogout={auth.logout}
        activeTab={activeTab}
        handleTabChange={tabChangeHandler}
      />
      {showConfetti && (
        <ConfettiCelebration
          onComplete={() => setShowConfetti(false)}
          duration={5000}
        />
      )}
    </AppContext.Provider>
  );
};
